import { formatDate } from '@angular/common';
import { DateFormats } from '../shared/constants';

/**
 * Date utility functions for consistent date formatting across the application.
 * Uses Angular's formatDate function for better performance and consistency.
 */
export class DateUtils {

    /**
     * Formats a date to YYYY-MM-DD format (ISO date string without time)
     * @param date - Date object, string, or number to format
     * @param locale - Locale to use for formatting (default: 'en-US')
     * @returns Formatted date string in YYYY-MM-DD format or null if invalid
     */
    static formatToISODate(date: Date | string | number | null | undefined, locale: string = 'nl-BE', timezone?: string): string | null {
        if (!date) return null;

        try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return null;

            return formatDate(dateObj, DateFormats.ISO_DATE, locale, timezone);
        } catch (error) {
            return null;
        }
    }

    /**
     * Formats a date to YYYYMMDD format (compact date string)
     * @param date - Date object, string, or number to format
     * @param locale - Locale to use for formatting (default: 'en-US')
     * @returns Formatted date string in YYYYMMDD format or null if invalid
     */
    static formatToCompactDate(date: Date | string | number | null | undefined, locale: string = 'nl-BE', timezone?: string): string | null {
        if (!date) return null;

        try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return null;

            return formatDate(dateObj, DateFormats.COMPACT_DATE, locale, timezone);
        } catch (error) {
            return null;
        }
    }

    /**
     * Formats a date to DD/MM/YYYY format (Belgian date format)
     * @param date - Date object, string, or number to format
     * @param locale - Locale to use for formatting (default: 'nl-BE')
     * @returns Formatted date string in DD/MM/YYYY format or null if invalid
     */
    static formatToBelgianDate(date: Date | string | number | null | undefined, locale: string = 'nl-BE', timezone?: string): string | null {
        if (!date) return null;

        try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return null;

            return formatDate(dateObj, DateFormats.BELGIAN_DATE, locale, timezone);
        } catch (error) {
            return null;
        }
    }

    /**
     * Formats a date to long format (e.g., "15 januari 2024" in Dutch, "January 15, 2024" in English)
     * Uses Angular's formatDate for consistency with other methods
     * @param date - Date object, string, or number to format
     * @param locale - Locale to use for formatting (default: 'nl-BE')
     * @returns Formatted date string in long format or null if invalid
     */
    static formatToLongDate(date: Date | string | number | null | undefined, locale: string = 'nl-BE', timezone?: string): string | null {
        if (!date) return null;

        try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return null;

            return formatDate(dateObj, DateFormats.LONG_DATE, locale, timezone);
        } catch (error) {
            return null;
        }
    }

    /**
     * Checks if a date is valid
     * @param date - Date object, string, or number to validate
     * @returns True if the date is valid, false otherwise
     */
    static isValidDate(date: Date | string | number | null | undefined): boolean {
        if (!date) return false;

        try {
            const dateObj = new Date(date);
            return !isNaN(dateObj.getTime());
        } catch (error) {
            return false;
        }
    }
}
